<template>
	<div class="dailyAnswerReport" :class="{ embeddedReport: embedded }" v-loading="loading">
		<header v-if="!embedded" class="reportHeader">
			<button class="backButton" type="button" @click="goBack">
				<i class="el-icon-arrow-left"></i>{{ backText }}
			</button>
			<div class="heading">
				<h2 :title="context.examTitle || reportTitle">{{ context.examTitle || reportTitle || defaultReportTitle }}</h2>
				<el-tag v-if="currentClassText" size="small" type="info">{{ currentClassText }}</el-tag>
			</div>
			<el-button type="primary" icon="el-icon-download" :disabled="!hasReport" @click="openExportDialog">导出 PDF</el-button>
		</header>

		<div v-if="hasReport" class="reportBody">
			<section class="overviewGrid">
				<div v-for="card in overviewCards" :key="card.label" class="overviewCard" :style="{ '--color': card.color }">
					<span>{{ card.label }}</span><b>{{ card.value }}</b>
				</div>
			</section>

			<section class="panel scoreDistributionPanel">
				<div class="panelTitle"><h3>成绩分段分布</h3><b>满分 {{ fullScoreStudents }}人</b></div>
				<div v-for="band in distributionBands" :key="band.label" class="bandRow">
					<span>{{ band.label }}</span><div><i :style="{ width: band.width + '%', background: band.color }"></i></div><b>{{ band.count }}人</b>
				</div>
			</section>

			<section class="panel scoreRosterPanel">
				<div class="panelTitle"><h3>成绩分段名单</h3></div>
				<el-table :data="scoreRosterRows" border class="scoreRosterTable">
					<el-table-column prop="grade" label="成绩等级" width="110" align="center">
						<template slot-scope="scope"><b :style="{ color: scope.row.color }">{{ scope.row.grade }}</b></template>
					</el-table-column>
					<el-table-column prop="range" label="成绩范围" width="135" align="center"></el-table-column>
					<el-table-column prop="count" label="人数" width="80" align="center">
						<template slot-scope="scope">{{ scope.row.count }}人</template>
					</el-table-column>
					<el-table-column label="学生名单" min-width="520">
						<template slot-scope="scope">
							<div v-if="scope.row.students.length" class="rosterNames">
								<span v-for="(student, index) in scope.row.students" :key="`${scope.row.key}-${student.studentId || index}-${index}`" :class="studentNameClass(student)">
									{{ student.studentName }}
								</span>
							</div>
							<span v-else class="emptyRoster">暂无</span>
						</template>
					</el-table-column>
				</el-table>
			</section>

			<section class="panel questionPanel">
				<div class="panelTitle"><h3>单题统计分析</h3></div>
				<el-table :data="questions" stripe border>
					<el-table-column prop="questionNumber" label="题号" width="70" align="center"></el-table-column>
					<el-table-column prop="answerCount" label="答题总数" width="92" align="center"></el-table-column>
					<el-table-column prop="rightCount" label="正确" width="72" align="center"></el-table-column>
					<el-table-column prop="wrongCount" label="错误" width="72" align="center"></el-table-column>
					<el-table-column prop="blankCount" label="空题" width="72" align="center"></el-table-column>
					<el-table-column prop="studentCount" label="答题人数" width="88" align="center"></el-table-column>
					<el-table-column label="正确率" width="92" align="center"><template slot-scope="scope"><b :style="{ color: rateColor(scope.row.rightRate) }">{{ formatRate(scope.row.rightRate) }}%</b></template></el-table-column>
					<el-table-column prop="testCenter" label="考点" min-width="260" show-overflow-tooltip></el-table-column>
				</el-table>
			</section>

			<section class="panel weakPanel">
				<div class="panelTitle"><h3>易错考点统计</h3></div>
				<div class="weakConclusion"><label>易错题 TOP 5</label><p>{{ weakQuestionNumbers }}</p></div>
				<div class="weakConclusion"><label>易错考点</label><p>{{ weakTestCenters }}</p></div>
			</section>
		</div>

		<el-empty v-else-if="!loading" description="当前班暂无可展示的答题统计"></el-empty>

		<el-dialog title="导出报告" :visible.sync="exportDialogVisible" width="720px" custom-class="dailyReportExportDialog" append-to-body :close-on-click-modal="false">
			<div class="exportDialogBody" v-loading="leaderLoading">
				<div class="identityChoices">
					<button type="button" :class="{ active: exportMode === 'teacher' }" @click="chooseExportMode('teacher')">
						<i class="el-icon-user"></i><span>班级老师</span>
					</button>
					<button type="button" :class="{ active: exportMode === 'leader' }" @click="chooseExportMode('leader')">
						<i class="el-icon-s-custom"></i><span>备课组长</span>
					</button>
				</div>
				<div v-if="!exportMode" class="identityHint">请选择导出身份</div>

				<div v-if="exportMode === 'teacher'" class="exportSummary">
					<i class="el-icon-school"></i>
					<div>
						<b>选择本人名下班级</b>
						<span>当前班级已默认勾选，可同时选择本人其他班级</span>
					</div>
				</div>
				<div v-if="exportMode === 'teacher'" class="ownClassSelector">
					<el-checkbox-group v-model="selectedOwnClassIds">
						<el-checkbox v-for="item in ownClasses" :key="item.bjId" :label="Number(item.bjId)" :disabled="Number(item.bjId) === Number(bjId)">
							{{ classLabel(item) }}（{{ item.totalStudents || 0 }}人）
						</el-checkbox>
					</el-checkbox-group>
					<el-empty v-if="!ownClasses.length" :image-size="55" description="暂无可选班级"></el-empty>
				</div>

				<div v-else-if="exportMode === 'leader'" class="leaderConfig">
					<el-alert type="info" :closable="false" show-icon title="当前老师和当前班级会自动包含；点击老师姓名可展开或收起班级。"></el-alert>
					<div class="teacherSelector">
						<label>关联老师</label>
						<el-select
							v-model="selectedTeacherIds"
							multiple
							filterable
							collapse-tags
							placeholder="选择同校关联老师"
							@change="teacherSelectionChanged">
							<el-option v-for="teacher in teacherOptions" :key="teacher.teacherId" :label="teacherOptionLabel(teacher)" :value="Number(teacher.teacherId)">
								<span>{{ teacher.teacherName }}</span><small>{{ maskAccount(teacher.teacherAccount) }} · {{ teacher.school || '未填写学校' }}</small>
							</el-option>
						</el-select>
					</div>

					<div v-if="memberTeachers.length" class="memberList">
						<div v-for="teacher in memberTeachers" :key="`member-${teacher.teacherId}`" class="memberCard">
							<button type="button" class="memberHeader" @click="toggleMember(teacher.teacherId)">
								<span><b>{{ teacher.teacherName || '未命名老师' }}</b><small v-if="teacher.isCurrent">当前老师</small></span>
								<strong>{{ (teacher.classes || []).length }}个班级<i :class="isMemberExpanded(teacher.teacherId) ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i></strong>
							</button>
							<div v-if="isMemberExpanded(teacher.teacherId)" class="memberClasses">
								<el-checkbox-group :value="memberSelectedIds(teacher)" @input="memberClassSelectionChanged(teacher, $event)">
									<el-checkbox v-for="item in teacher.classes || []" :key="item.bjId" :label="Number(item.bjId)" :disabled="teacher.isCurrent && Number(item.bjId) === Number(bjId)">
										{{ classLabel(item) }}（{{ item.totalStudents || 0 }}人）
									</el-checkbox>
								</el-checkbox-group>
							</div>
						</div>
					</div>
					<el-alert v-else type="warning" :closable="false" show-icon title="请输入并关联至少一名同校老师账户。"></el-alert>
				</div>
			</div>
			<span slot="footer" class="dialogFooter">
				<el-button @click="exportDialogVisible = false">取消</el-button>
				<el-button type="primary" :disabled="!exportMode" :loading="exporting" @click="confirmExport">确认</el-button>
			</span>
		</el-dialog>
	</div>
</template>

<script>
import fileDownload from 'js-file-download';

export default {
	name: 'dailyAnswerReport',
	props: {
		bjId: [String, Number],
		dailyReportExam: Object,
		reportSource: String,
		paperSide: String,
		computerizedPaperId: [String, Number],
		textbookId: [String, Number],
		userTextbookId: [String, Number],
		chapterReportItem: Object,
		embedded: { type: Boolean, default: false }
	},
	data() {
		return {
			loading: false,
			exporting: false,
			exportDialogVisible: false,
			exportMode: '',
			leaderLoading: false,
							leaderLoaded: false,
							profile: { registered: false, classes: [], classIds: [] },
							teacherOptions: [],
							selectedTeacherIds: [],
							selectedClassIds: [],
							selectedOwnClassIds: [],
			memberExpandedIds: [],
			context: {},
			summary: {},
			questions: [],
			classes: [],
			classQuestions: [],
			scoreDistribution: [],
			scoreStudents: []
		};
	},
	computed: {
		isTextbookChapter() { return this.reportSource === 'textbookChapter'; },
		isComputerized() { return this.reportSource === 'computerized'; },
		reportTitle() {
			if (this.isTextbookChapter) return this.chapterReportItem && this.chapterReportItem.displayName;
			return this.dailyReportExam && this.dailyReportExam.title;
		},
		examId() {
			if (this.isTextbookChapter) return this.chapterReportItem && Number(this.chapterReportItem.textbookDirectoryId);
			if (this.isComputerized) return Number(this.computerizedPaperId || (this.dailyReportExam && this.dailyReportExam.examId));
			return this.dailyReportExam && Number(this.dailyReportExam.examId);
		},
		backText() {
			if (this.isTextbookChapter) return '返回整本统计';
			const labels = { college: '返回高考真题', classExam: '返回班级试卷', computerized: '返回机考套题' };
			return labels[this.reportSource] || '返回每日一听';
		},
		defaultReportTitle() {
			if (this.isTextbookChapter) return '课本章节答题统计';
			const labels = { college: '高考真题答题统计', classExam: '班级试卷答题统计', computerized: '机考套题答题统计' };
			return labels[this.reportSource] || '每日一听答题统计';
		},
		reportApi() { return this.isTextbookChapter ? 'api/app/yydTextbookReport/chapterDashboard' : 'api/app/yydTeacherDailyReport/dashboard'; },
		exportApi() { return this.isTextbookChapter ? 'api/app/yydTextbookReport/chapterExportPdf' : 'api/app/yydTeacherDailyReport/exportPdf'; },
		hasReport() { return Boolean(this.context && this.context.examId); },
		currentClass() { return this.classes.length ? this.classes[0] : null; },
		currentClassText() { return this.currentClass ? this.classLabel(this.currentClass) : ''; },
		selectedTeachers() {
			return this.selectedTeacherIds.map(id => this.teacherOptions.find(item => Number(item.teacherId) === Number(id))).filter(Boolean);
		},
		ownClasses() { return this.profile.ownClasses || []; },
		memberTeachers() {
			const current = this.profile.teacher || {};
			const currentTeacher = {
				teacherId: Number(current.teacherId || 0),
				teacherName: current.teacherName || '当前老师',
				teacherAccount: current.teacherAccount,
				classes: this.ownClasses,
				isCurrent: true
			};
			return [currentTeacher, ...this.selectedTeachers.map(item => ({ ...item, isCurrent: false }))];
		},
		overviewCards() {
			const s = this.summary || {};
			return [
				{ label: '题目数量', value: `${this.questions.length}题`, color: '#1d7b3a' },
				{ label: '答题人数', value: `${s.studentCount || 0}/${s.totalStudents || 0}人`, color: '#3377e6' },
				{ label: '正确率', value: `${this.formatRate(s.rightRate)}%`, color: '#20a878' },
				{ label: '平均分', value: `${this.formatScore(s.averageScore)}分`, color: '#7656d6' },
				{ label: '答题总数', value: `${s.totalAnswerCount || 0}次`, color: '#45a3ff' },
				{ label: '正确总数', value: `${s.rightCount || 0}次`, color: '#2a9d8f' },
				{ label: '错误总数', value: `${s.wrongCount || 0}次`, color: '#f28b3c' },
				{ label: '空题总数', value: `${s.blankCount || 0}次`, color: '#e56b6f' }
			];
		},
		totalUnanswered() { return this.classes.reduce((sum, item) => sum + Number(item.unansweredCount || 0), 0); },
		unansweredNames() {
			const result = [];
			this.classes.forEach(item => String(item.unansweredNames || '').split('、').filter(Boolean).forEach(name => result.push(name)));
			return result;
		},
		fullScore() {
			return this.scoreStudents.reduce((max, item) => Math.max(max, Number(item.fullScore || 0)), 0)
				|| this.scoreDistribution.reduce((max, item) => Math.max(max, Number(item.fullScore || 0)), 0)
				|| 100;
		},
		fullScoreStudents() { return this.scoreDistribution.reduce((sum, item) => sum + Number(item.fullScoreStudents || 0), 0); },
		distributionBands() {
			const rawCounts = [1, 2, 3, 4, 5].map(index => this.scoreDistribution.reduce((sum, item) => sum + Number(item[`scoreBand${index}`] || 0), 0));
			const ranges = this.scoreRanges;
			const rows = [
				{ label: `优秀 ${ranges.excellent}`, count: rawCounts[4], color: '#1f9969' },
				{ label: `良好 ${ranges.good}`, count: rawCounts[3], color: '#3494a7' },
				{ label: `中等 ${ranges.medium}`, count: rawCounts[2], color: '#4976b5' },
				{ label: `一般 ${ranges.general}`, count: rawCounts[1], color: '#e09737' },
				{ label: `不及格 ${ranges.fail}`, count: rawCounts[0], color: '#cb5b5b' }
			];
			const max = Math.max(1, ...rows.map(item => item.count));
			return rows.map(item => ({ ...item, width: item.count * 100 / max }));
		},
		scoreRanges() {
			const full = this.fullScore;
			return {
				excellent: `${this.shortScore(full * 5 / 6)}–${this.shortScore(full)}分`,
				good: `${this.shortScore(full * 2 / 3)}–${this.shortScore(full * 5 / 6)}分`,
				medium: `${this.shortScore(full / 2)}–${this.shortScore(full * 2 / 3)}分`,
				general: `${this.shortScore(full / 3)}–${this.shortScore(full / 2)}分`,
				fail: `0–${this.shortScore(full / 3)}分`
			};
		},
		scoreRosterRows() {
			const configs = [
				{ key: 'excellent', grade: '优秀', color: '#1f9969' },
				{ key: 'good', grade: '良好', color: '#3494a7' },
				{ key: 'medium', grade: '中等', color: '#4976b5' },
				{ key: 'general', grade: '一般', color: '#e09737' },
				{ key: 'fail', grade: '不及格', color: '#cb5b5b' }
			];
			const groups = { excellent: [], good: [], medium: [], general: [], fail: [] };
			this.scoreStudents.forEach(source => {
				const student = { ...source, score: Number(source.score || 0), fullScore: Number(source.fullScore || this.fullScore) };
				groups[this.scoreBandKey(student.score, student.fullScore)].push(student);
			});
			Object.keys(groups).forEach(key => groups[key].sort((a, b) => Number(b.score) - Number(a.score) || String(a.studentName || '').localeCompare(String(b.studentName || ''), 'zh-CN')));
			const rows = configs.map(item => ({ ...item, range: this.scoreRanges[item.key], count: groups[item.key].length, students: groups[item.key] }));
			rows.push({
				key: 'unanswered', grade: '未答题', range: '未作答', color: '#87919c', count: this.totalUnanswered,
				students: this.unansweredNames.map((name, index) => ({ studentId: `unanswered-${index}`, studentName: name, score: null, fullScore: this.fullScore }))
			});
			return rows;
		},
		weakRows() {
			return this.weakest(this.questions.filter(item => Number(item.answerCount || 0) > 0 || Number(item.studentCount || 0) > 0));
		},
		weakQuestionNumbers() { return this.weakRows.length ? this.weakRows.map(item => `第${item.questionNumber}题`).join('、') : '暂无'; },
		weakTestCenters() { return this.weakRows.length ? this.testCenters(this.weakRows) : '暂无'; }
	},
	methods: {
		previewParams() {
			if (this.isTextbookChapter) {
				return {
					textbookId: Number(this.textbookId),
					userTextbookId: Number(this.userTextbookId),
					 directoryId: this.examId,
					 bjId: Number(this.bjId),
					 mode: 'teacher',
					 bjIds: String(Number(this.bjId))
				};
			}
			return {
				examId: this.examId,
				bjId: Number(this.bjId),
				mode: 'teacher',
				bjIds: String(Number(this.bjId)),
				reportSource: this.reportSource || 'daily',
				paperSide: this.paperSide || undefined
			};
		},
		goBack() {
			if (this.isTextbookChapter) this.$emit('chapter-back');
			else this.$emit('report-back', this.reportSource || 'daily');
		},
		async initialize() {
			if (!this.examId || !this.bjId || (this.isTextbookChapter && (!this.textbookId || !this.userTextbookId))) {
				this.$message.warning(this.isTextbookChapter ? '缺少课本章节或班级信息' : '缺少每日一听套题或班级信息');
				return;
			}
			await this.loadReport();
		},
		async loadReport() {
			this.loading = true;
			try {
				const res = await this.$http.get(this.reportApi, { params: this.previewParams() });
				const data = res.data || {};
				this.context = data.context || {};
				this.summary = data.summary || {};
				this.questions = data.questions || [];
				this.classes = data.classes || [];
				this.classQuestions = data.classQuestions || [];
				this.scoreDistribution = data.scoreDistribution || [];
				this.scoreStudents = data.scoreStudents || [];
			} catch (error) {
				const message = error && error.response && error.response.data && error.response.data.msg;
				this.$message.error(message || '答题报告加载失败，请稍后重试');
			} finally { this.loading = false; }
		},
		async openExportDialog() {
			this.exportMode = '';
			this.selectedOwnClassIds = [Number(this.bjId)];
			this.exportDialogVisible = true;
			await this.loadLeaderProfile();
		},
		chooseExportMode(mode) {
			this.exportMode = mode;
			if (mode === 'leader') {
				this.loadLeaderProfile();
				const currentTeacherId = Number((this.profile.teacher || {}).teacherId || 0);
				if (currentTeacherId) this.memberExpandedIds = [currentTeacherId];
			}
		},
		toggleMember(teacherId) {
			const id = Number(teacherId);
			const expanded = new Set(this.memberExpandedIds.map(Number));
			if (expanded.has(id)) expanded.delete(id); else expanded.add(id);
			this.memberExpandedIds = Array.from(expanded);
		},
		isMemberExpanded(teacherId) { return this.memberExpandedIds.includes(Number(teacherId)); },
		memberSelectedIds(teacher) {
			const ids = new Set((teacher.classes || []).map(item => Number(item.bjId)));
			const source = teacher.isCurrent ? this.selectedOwnClassIds : this.selectedClassIds;
			return source.map(Number).filter(id => ids.has(id));
		},
		memberClassSelectionChanged(teacher, ids) {
			const selected = Array.from(new Set((ids || []).map(Number)));
			if (teacher.isCurrent) {
				this.selectedOwnClassIds = Array.from(new Set([...selected, Number(this.bjId)])).filter(id => id > 0);
				return;
			}
			const teacherClassIds = new Set((teacher.classes || []).map(item => Number(item.bjId)));
			const otherSelected = this.selectedClassIds.map(Number).filter(id => !teacherClassIds.has(id));
			this.selectedClassIds = Array.from(new Set([...otherSelected, ...selected]));
		},
		async exportModeChanged(mode) {
			if (mode === 'leader') await this.loadLeaderProfile();
		},
		async loadLeaderProfile(force = false) {
			if (this.leaderLoaded && !force) return;
			this.leaderLoading = true;
			try {
				const res = await this.$http.get('api/app/yydTeacherDailyReport/profile');
				this.profile = res.data || this.profile;
				const optionsRes = await this.$http.get('api/app/yydTeacherDailyReport/teacherOptions');
				const currentTeacherId = Number((this.profile.teacher || {}).teacherId || 0);
				const optionMap = new Map();
				(optionsRes.data || []).filter(item => Number(item.teacherId) !== currentTeacherId).forEach(item => optionMap.set(Number(item.teacherId), item));
				const grouped = new Map();
				(this.profile.classes || []).forEach(item => {
					const teacherId = Number(item.teacherId);
					if (!grouped.has(teacherId)) grouped.set(teacherId, { teacherId, teacherName: item.teacherName, teacherAccount: item.teacherAccount, school: item.school, classes: [] });
					grouped.get(teacherId).classes.push(item);
				});
				grouped.forEach((savedTeacher, id) => {
					if (!optionMap.has(id)) optionMap.set(id, savedTeacher);
				});
				this.teacherOptions = Array.from(optionMap.values());
				this.selectedTeacherIds = Array.from(grouped.keys());
				this.selectedClassIds = (this.profile.classIds || []).map(Number);
				if (!this.selectedOwnClassIds.length) this.selectedOwnClassIds = [Number(this.bjId)];
				this.memberExpandedIds = [
					Number((this.profile.teacher || {}).teacherId || 0),
					...this.selectedTeacherIds.map(Number)
				].filter(id => id > 0);
				this.leaderLoaded = true;
			} finally { this.leaderLoading = false; }
		},
		teacherSelectionChanged(ids) {
			const selected = new Set(ids.map(Number));
			const allowed = new Set();
			this.teacherOptions.filter(item => selected.has(Number(item.teacherId))).forEach(teacher => {
				const classIds = (teacher.classes || []).map(item => Number(item.bjId));
				classIds.forEach(id => allowed.add(id));
				if (!classIds.some(id => this.selectedClassIds.includes(id))) this.selectedClassIds.push(...classIds);
			});
			this.selectedClassIds = Array.from(new Set(this.selectedClassIds.map(Number))).filter(id => allowed.has(id));
		},
		async confirmExport() {
			if (this.exportMode === 'teacher') {
				this.selectedOwnClassIds = Array.from(new Set([
					...this.selectedOwnClassIds.map(Number), Number(this.bjId)
				])).filter(id => id > 0);
				if (!this.selectedOwnClassIds.length) { this.$message.warning('请至少选择一个本人名下班级'); return; }
			}
			if (this.exportMode === 'leader') {
				if (!this.leaderLoaded) await this.loadLeaderProfile();
				if (!this.selectedTeacherIds.length) { this.$message.warning('请先关联至少一名同校老师'); return; }
				if (!this.selectedClassIds.length) { this.$message.warning('请至少选择一个组员班级'); return; }
			}
			this.exporting = true;
			try {
				if (this.exportMode === 'leader') {
					const profileRes = await this.$http.put('api/app/yydTeacherDailyReport/profile', { bjIds: this.selectedClassIds });
					this.profile = profileRes.data || this.profile;
				}
				const ownClassIds = this.selectedOwnClassIds;
				const params = this.isTextbookChapter
					? { textbookId: Number(this.textbookId), userTextbookId: Number(this.userTextbookId), directoryId: this.examId, bjId: Number(this.bjId), mode: this.exportMode, bjIds: ownClassIds.join(',') }
					: {
						examId: this.examId,
						bjId: Number(this.bjId),
						mode: this.exportMode,
						bjIds: ownClassIds.join(','),
						reportSource: this.reportSource || 'daily',
						paperSide: this.paperSide || undefined
					};
				const blob = await this.$http.get(this.exportApi, { params, responseType: 'blob' });
				fileDownload(blob, `${this.context.examTitle || this.defaultReportTitle}-答题报告.pdf`);
				this.exportDialogVisible = false;
				this.$message.success('PDF 答题报告已导出');
			} finally { this.exporting = false; }
		},
		scoreBandKey(score, fullScore) {
			if (!fullScore || score <= fullScore / 3) return 'fail';
			if (score <= fullScore / 2) return 'general';
			if (score <= fullScore * 2 / 3) return 'medium';
			if (score <= fullScore * 5 / 6) return 'good';
			return 'excellent';
		},
		studentNameClass(student) {
			if (student.score === null || student.score === undefined) return '';
			if (Number(student.score) === Number(student.fullScore)) return 'fullScoreName';
			if (Number(student.score) === 0) return 'zeroScoreName';
			return '';
		},
		teacherOptionLabel(teacher) {
			const account = this.maskAccount(teacher.teacherAccount);
			return account ? `${teacher.teacherName}（${account}）` : teacher.teacherName;
		},
		maskAccount(value) {
			const account = String(value || '').trim();
			if (!account) return '账号未填写';
			if (/^\d{11}$/.test(account)) return `${account.slice(0, 3)}****${account.slice(-4)}`;
			if (account.length > 7) return `${account.slice(0, 3)}***${account.slice(-3)}`;
			return account;
		},
		classLabel(item) { return `${item.school ? item.school + ' · ' : ''}${item.grade || ''}${item.className || ''}`; },
		weakest(rows) { return [...rows].sort((a, b) => Number(a.rightRate || 0) - Number(b.rightRate || 0) || Number(b.wrongCount || 0) - Number(a.wrongCount || 0)).slice(0, 5); },
		testCenters(rows) {
			const values = [];
			rows.forEach(item => String(item.testCenter || '').split(/[、,，;；|]+/).filter(value => value && value !== '未标注').forEach(value => { if (!values.includes(value)) values.push(value); }));
			return values.length ? values.join('、') : '未标注';
		},
		formatRate(value) { return Number(value || 0).toFixed(2); },
		formatScore(value) { return Number(value || 0).toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1'); },
		shortScore(value) { return Number(value || 0).toFixed(1).replace(/\.0$/, ''); },
		rateColor(value) { const rate = Number(value || 0); return rate < 60 ? '#e56b6f' : rate < 80 ? '#e6a23c' : '#20a878'; }
	},
	mounted() { this.initialize(); }
};
</script>

<style lang="scss" scoped>
.dailyAnswerReport { width: 100%; max-height: calc(100vh - 210px); min-height: 650px; overflow-y: auto; overflow-x: hidden; padding-bottom: 30px; box-sizing: border-box; color: #28323e; background: #f6f8fb; }
.dailyAnswerReport.embeddedReport { max-height: none; min-height: 560px; overflow: visible; }
.reportHeader { min-height: 84px; padding: 13px 18px; display: flex; align-items: center; gap: 12px; background: #fff; border-bottom: 1px solid #e8edf3; position: sticky; top: 0; z-index: 5; box-sizing: border-box; }
.backButton { border: 0; background: transparent; cursor: pointer; white-space: nowrap; color: #3d4854; font-size: 14px; }
.heading { flex: 1; min-width: 0; display: flex; align-items: center; gap: 12px; h2 { margin: 0; font-size: 19px; line-height: 1.35; white-space: normal; word-break: break-all; } }
.reportBody { padding-top: 16px; }
.overviewGrid { margin: 0 16px 16px; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
.overviewCard { min-height: 88px; padding: 14px 16px; box-sizing: border-box; border: 1px solid #e3e9ef; border-top: 3px solid var(--color); border-radius: 9px; background: #fff; span { color: #76818d; font-size: 13px; } b { display: block; margin-top: 10px; color: var(--color); font-size: 23px; } }
.panel { margin: 0 16px 16px; padding: 17px; border: 1px solid #e3e9ef; border-radius: 9px; background: #fff; box-shadow: 0 3px 12px rgba(28, 48, 70, .04); }
.panelTitle { margin-bottom: 15px; display: flex; justify-content: space-between; align-items: center; h3 { margin: 0; font-size: 17px; } > b { color: #1d7b3a; font-size: 14px; } }
.bandRow { display: grid; grid-template-columns: 145px 1fr 48px; gap: 10px; align-items: center; margin: 13px 0; font-size: 13px; > div { height: 9px; overflow: hidden; border-radius: 5px; background: #edf1f4; i { display: block; height: 100%; border-radius: 5px; } } > b { text-align: right; } }
.scoreRosterPanel { padding-bottom: 15px; }
.scoreRosterTable ::v-deep td { padding: 12px 0; }
.rosterNames { display: flex; flex-wrap: wrap; gap: 6px 14px; line-height: 1.8; span { white-space: nowrap; } }
.fullScoreName { color: #15965d; font-weight: 600; }
.zeroScoreName { color: #d84f4f; font-weight: 600; }
.emptyRoster { color: #a0a8b2; }
.weakConclusion { display: grid; grid-template-columns: 125px 1fr; border: 1px solid #e4e9ef; border-bottom: 0; &:last-child { border-bottom: 1px solid #e4e9ef; } label { padding: 12px; text-align: center; background: #f4effb; } p { margin: 0; padding: 12px; line-height: 1.65; } }
.exportDialogBody { min-height: 160px; }
.identityChoices { display: flex; justify-content: center; gap: 34px; margin: 8px auto 24px; max-width: 540px; button { width: 230px; height: 76px; padding: 0 24px; display: flex; align-items: center; justify-content: center; gap: 13px; border: 1px solid #d9e0e7; border-radius: 8px; background: #fff; color: #394553; cursor: pointer; font-size: 19px; font-weight: 600; box-shadow: 0 4px 14px rgba(31, 55, 78, .06); transition: border-color .18s, color .18s, background .18s, box-shadow .18s; i { font-size: 25px; } &:hover { border-color: #72a786; color: #1d7b3a; box-shadow: 0 6px 18px rgba(29, 123, 58, .1); } } button.active { border-color: #1d7b3a; background: #f0f8f2; color: #1d7b3a; box-shadow: 0 6px 18px rgba(29, 123, 58, .12); } }
.identityHint { margin: -4px 0 18px; color: #89929d; text-align: center; font-size: 13px; }
.exportSummary { display: flex; align-items: center; gap: 14px; padding: 18px; border: 1px solid #dbe8df; border-radius: 8px; background: #f8fcf9; i { color: #1d8b57; font-size: 28px; } b, span { display: block; } span { margin-top: 6px; color: #77828e; } }
.ownClassSelector { margin: 0 0 20px; padding: 14px 18px; border: 1px solid #e2e8ee; border-radius: 8px; background: #fff; .el-checkbox { margin: 8px 18px 8px 0; } }
.leaderOwnClasses { margin-bottom: 18px; padding: 14px 18px; border: 1px solid #e2e8ee; border-radius: 8px; background: #fff; .sectionLabel { margin-bottom: 8px; color: #4b5662; font-weight: 600; } .el-checkbox { margin: 8px 18px 8px 0; } }
.leaderConfig .el-alert { margin-bottom: 18px; }
.teacherSelector { display: flex; align-items: center; gap: 15px; margin-bottom: 18px; label { width: 110px; flex: none; font-weight: 600; } .el-select { flex: 1; } }
::v-deep .el-select-dropdown__item small { float: right; color: #9aa2ad; }
.memberList { display: flex; flex-direction: column; gap: 8px; max-height: 330px; overflow-y: auto; }
.memberCard { padding: 14px; border: 1px solid #e2e8ee; border-radius: 8px; background: #fff; }
.memberHeader { width: 100%; padding: 0; display: flex; justify-content: space-between; align-items: center; border: 0; background: transparent; color: #28323e; cursor: pointer; text-align: left; b { font-size: 15px; } small { margin-left: 8px; color: #1d8b57; font-size: 12px; } strong { color: #7b8793; font-size: 12px; font-weight: 400; } i { margin-left: 5px; } }
.memberClasses { margin-top: 12px; padding-top: 8px; border-top: 1px solid #edf0f3; }
.memberClasses ::v-deep .el-checkbox { display: block; margin: 8px 0; white-space: normal; line-height: 1.45; }
@media (max-width: 1280px) { .overviewGrid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 600px) {
	::v-deep .dailyReportExportDialog { width: calc(100vw - 24px) !important; margin: 6px auto !important; }
	::v-deep .dailyReportExportDialog .el-dialog__header { padding: 16px 16px 8px; }
	::v-deep .dailyReportExportDialog .el-dialog__title { font-size: 18px; }
	::v-deep .dailyReportExportDialog .el-dialog__body { padding: 10px 14px 4px; }
	::v-deep .dailyReportExportDialog .el-dialog__footer { padding: 8px 14px 14px; }
	.identityChoices { gap: 12px; margin: 4px 0 18px; button { flex: 1; width: auto; max-width: none; height: 68px; padding: 0 12px; gap: 8px; font-size: 16px; i { font-size: 21px; } } }
	.exportSummary { padding: 12px; gap: 9px; i { font-size: 22px; } b { font-size: 14px; } span { font-size: 12px; line-height: 1.45; } }
	.ownClassSelector, .leaderOwnClasses { padding: 10px 12px; margin-bottom: 12px; }
	.ownClassSelector .el-checkbox, .leaderOwnClasses .el-checkbox { display: block; margin: 9px 0; white-space: normal; line-height: 1.45; }
	.teacherSelector { display: block; margin-bottom: 12px; label { display: block; width: auto; margin-bottom: 7px; } .el-select { width: 100%; } }
	.memberList { max-height: 260px; gap: 8px; }
	.memberCard { padding: 10px 12px; }
}
</style>
